/**
 * C04 Badge UI — two surfaces:
 *   1. `renderBadgeRevealBanner` — kid-facing home banner that fires once when
 *      `lastUnlocked` is set; consumes the marker so it doesn't re-fire on
 *      subsequent home renders.
 *   2. `renderBadgeDashboardPanel` — parent dashboard panel listing all earned
 *      badges + their "媽媽看得懂" blurbs and locked-but-visible peek targets.
 *
 * Inline styles (matches dayone-banner / streak-ui pattern). No external CSS.
 * Pure presentation — all state mutation goes through `badges.ts`.
 */

import type { PlayerProgress } from '../../shared/types';
import {
  BADGE_CATALOGUE,
  CATEGORY_LABELS,
  buildBadgeSnapshot,
  consumeLastUnlocked,
  snapshotByCategory,
  type BadgeCategory,
  type BadgeDef,
} from './badges';

const STYLE_ID = 'badges-ui-style';

function ensureStyleInjected(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes badge-reveal-pop {
      0%   { transform: scale(0.6) rotate(-12deg); opacity: 0; }
      55%  { transform: scale(1.15) rotate(6deg); opacity: 1; }
      100% { transform: scale(1) rotate(0); opacity: 1; }
    }
    .badge-reveal-banner {
      animation: badge-reveal-pop 0.55s ease-out;
    }
    @media (prefers-reduced-motion: reduce) {
      .badge-reveal-banner { animation: none !important; }
    }
  `;
  document.head.appendChild(style);
}

// ── Home reveal banner ──────────────────────────────────────────────────────

/**
 * Render a one-shot "🎉 解鎖新徽章!" banner if `p.badges.lastUnlocked` is set.
 * Consumes the marker (clears + persists) so subsequent home renders don't
 * re-fire. Returns the unmount function (no-op when nothing was unlocked).
 *
 * Container is cleared before render — call with the same slot on every
 * home render (idempotent across renders).
 */
export function renderBadgeRevealBanner(
  container: HTMLElement,
  p: PlayerProgress,
): () => void {
  container.innerHTML = '';
  ensureStyleInjected();
  const def = consumeLastUnlocked(p);
  if (!def) return () => { /* noop */ };

  const root = document.createElement('div');
  root.className = 'badge-reveal-banner';
  root.setAttribute('data-testid', 'badge-reveal-banner');
  root.setAttribute('data-badge-id', def.id);
  root.style.cssText = [
    'display:flex',
    'align-items:center',
    'gap:14px',
    'padding:14px 18px',
    'margin:8px 0',
    'border-radius:14px',
    'background:linear-gradient(135deg,#FFF59D 0%,#FFB74D 100%)',
    'border:2px solid #F57F17',
    'box-shadow:0 3px 8px rgba(0,0,0,0.18)',
    'color:#3E2723',
    'font-family:inherit',
  ].join(';');

  const icon = document.createElement('div');
  icon.textContent = def.icon;
  icon.style.cssText = 'font-size:40px;line-height:1;flex:0 0 auto;';

  const body = document.createElement('div');
  body.style.cssText = 'flex:1;display:flex;flex-direction:column;gap:2px;';

  const headline = document.createElement('div');
  headline.style.cssText = 'font-size:17px;font-weight:700;line-height:1.2;';
  headline.textContent = `🎉 解鎖新徽章:${def.name}`;

  const sub = document.createElement('div');
  sub.style.cssText = 'font-size:13px;line-height:1.3;color:#5D4037;';
  sub.textContent = def.description;

  body.append(headline, sub);
  root.append(icon, body);
  container.appendChild(root);

  return () => { root.remove(); };
}

// ── Parent dashboard panel ──────────────────────────────────────────────────

/**
 * Render the "🏅 徽章成就" panel for the parent dashboard. Lists earned badges
 * with the parentBlurb, then shows locked targets so the parent can see what
 * the kid is working toward.
 *
 * Read-only — does not mutate `p`.
 */
export function renderBadgeDashboardPanel(
  container: HTMLElement,
  p: PlayerProgress,
): void {
  const snapshot = buildBadgeSnapshot(p);
  const earned = snapshot.filter((s) => s.earned);
  const locked = snapshot.filter((s) => !s.earned);
  const byCategory = snapshotByCategory(p);

  const root = document.createElement('div');
  root.className = 'parent-section badge-dashboard';
  root.setAttribute('data-testid', 'badge-dashboard');
  root.setAttribute('data-earned-count', String(earned.length));
  root.setAttribute('data-total-count', String(BADGE_CATALOGUE.length));

  const header = document.createElement('h3');
  header.textContent = `🏅 徽章成就 (${earned.length} / ${BADGE_CATALOGUE.length})`;
  root.appendChild(header);

  if (earned.length === 0) {
    const empty = document.createElement('p');
    empty.style.cssText = 'color:#888;font-size:13px;';
    empty.textContent = '尚未獲得徽章 — 完成第一個學習章節即可解鎖。';
    root.appendChild(empty);
  }

  // Earned list — parent blurb is the "媽媽看得懂" copy
  if (earned.length > 0) {
    const earnedHeading = document.createElement('div');
    earnedHeading.style.cssText = 'font-size:14px;font-weight:600;margin:8px 0 6px;color:#388e3c;';
    earnedHeading.textContent = `✅ 已獲得 (${earned.length})`;
    root.appendChild(earnedHeading);

    const earnedList = document.createElement('ul');
    earnedList.className = 'dash-list';
    earnedList.setAttribute('data-testid', 'badges-earned-list');
    earnedList.style.cssText = 'list-style:none;padding:0;margin:0;';
    for (const entry of earned) {
      const li = document.createElement('li');
      li.setAttribute('data-badge-id', entry.def.id);
      li.style.cssText = 'display:flex;align-items:flex-start;gap:10px;padding:6px 0;border-bottom:1px solid #eee;';
      const iconEl = document.createElement('span');
      iconEl.textContent = entry.def.icon;
      iconEl.style.cssText = 'font-size:24px;line-height:1;flex:0 0 auto;';
      const text = document.createElement('div');
      text.style.cssText = 'flex:1;';
      const nameEl = document.createElement('div');
      nameEl.style.cssText = 'font-size:14px;font-weight:600;color:#333;';
      nameEl.textContent = `${entry.def.name} · ${CATEGORY_LABELS[entry.def.category]}`;
      const blurb = document.createElement('div');
      blurb.style.cssText = 'font-size:12px;color:#666;line-height:1.4;';
      blurb.textContent = entry.def.parentBlurb;
      text.append(nameEl, blurb);
      li.append(iconEl, text);
      earnedList.appendChild(li);
    }
    root.appendChild(earnedList);
  }

  // Upcoming peek — show up to 5 locked badges grouped by category
  if (locked.length > 0) {
    const upcomingHeading = document.createElement('div');
    upcomingHeading.style.cssText = 'font-size:14px;font-weight:600;margin:14px 0 6px;color:#666;';
    upcomingHeading.textContent = `🔒 還沒解鎖 (${locked.length})`;
    root.appendChild(upcomingHeading);

    const upcomingList = document.createElement('ul');
    upcomingList.className = 'dash-list';
    upcomingList.setAttribute('data-testid', 'badges-locked-list');
    upcomingList.style.cssText = 'list-style:none;padding:0;margin:0;';
    // Show all locked badges (catalogue is small, ≤10)
    for (const entry of locked) {
      const li = document.createElement('li');
      li.setAttribute('data-badge-id', entry.def.id);
      li.style.cssText = 'display:flex;align-items:center;gap:10px;padding:4px 0;color:#999;';
      const iconEl = document.createElement('span');
      iconEl.textContent = entry.def.icon;
      iconEl.style.cssText = 'font-size:20px;line-height:1;flex:0 0 auto;filter:grayscale(1);opacity:0.55;';
      const text = document.createElement('span');
      text.style.cssText = 'font-size:13px;';
      text.textContent = `${entry.def.name} — ${entry.def.description}`;
      li.append(iconEl, text);
      upcomingList.appendChild(li);
    }
    root.appendChild(upcomingList);
  }

  // Category breakdown footer (compact tally)
  const breakdownLine = document.createElement('div');
  breakdownLine.style.cssText = 'margin-top:10px;font-size:12px;color:#888;';
  const tallies = (Object.keys(byCategory) as BadgeCategory[]).map((cat) => {
    const grp = byCategory[cat];
    const e = grp.filter((b) => b.earned).length;
    return `${CATEGORY_LABELS[cat]} ${e}/${grp.length}`;
  });
  breakdownLine.textContent = tallies.join(' · ');
  root.appendChild(breakdownLine);

  container.appendChild(root);
}

/** Re-export the catalogue for callers that need to render previews elsewhere. */
export { BADGE_CATALOGUE };
export type { BadgeDef };
